import { ClickEvent, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, Image, Label, Panel, RepeatItemDisplayEvent, Snackbar, Tab, TableRow, TableRowDisplayEvent, Textbox, Toast } from "@mcleod/components";
import { AutogenLayoutFeasibility } from "./autogen/AutogenLayoutFeasibility";
import { PlanningUtil } from "../planning/PlanningUtil";
import { DatePart, DateUtil, HorizontalAlignment, ModelRow, StringUtil, MapSettings } from "@mcleod/core";
import { RepeatItem } from "@mcleod/components/src/components/repeater/RepeatItem";
import { ImageName } from "@mcleod/images";
import { TrimbleMap } from "@mcleod/components/src/components/map/TrimbleMap";
import { Preassignment, PreassignmentSlideoutProps } from "./Preassignment";
import { PreassignmentDialog } from "./PreassignmentDialog";
import { MobileCommSendMessage } from "../mobilecomm/MobileCommSendMessage";
import { SendLoad } from "./SendLoad";
import { getDispatchControlValue } from "@mcleod/general";
import { CommonTooltips } from "@mcleod/common";
import { AppointmentUtil, MultiStop } from "..";

const dutyTypes = {
    "OFF DUTY": {
        color: "strokeSecondary",
        top: "50px",
    },
    "SLEEPING": {
        color: "#663399",
        top: "70px",
    },
    "DRIVING": {
        color: "primary.dark",
        top: "90px",
    },
    "ON DUTY": {
        color: "primary.lighter",
        top: "110px",
    }
};

export interface FeasibilityProps {
    availableMoveId: string;
    equipMoveId?: string;
    originEarly: Date;
    destEarly: Date;
    driverId: string;
    linkedId?: string;
}

export class Feasibility extends AutogenLayoutFeasibility {
    private isPreassignnValid: boolean;
    private availableMoveId: string;
    private equipMoveId: string;
    private originEarly: Date;
    private destEarly: Date;
    private driverId: string;
    private linkedId: string;

    onLoad(): void | Promise<void> {
        this.setTabStylingForExpand(this.tabHoursOfService);
        this.setTabStylingForExpand(this.tabChecklist);
    }

    setup(props: FeasibilityProps) {
        const { availableMoveId, equipMoveId, originEarly, destEarly, driverId, linkedId } = props;
        this.availableMoveId = availableMoveId;
        this.equipMoveId = equipMoveId;
        this.originEarly = originEarly;
        this.destEarly = destEarly;
        this.driverId = driverId;
        this.linkedId = linkedId;
        this.searchData();
    }

    searchData() {
        this.mainDataSource.search({
            available_move_id: this.availableMoveId,
            equip_move_id: this.equipMoveId, driver_id: this.driverId,
            origin_early: this.originEarly, dest_early: this.destEarly
        });
        this.searchDriverSources(this.driverId);
    }

    setTabStylingForExpand(tab: Tab) {
        tab.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
        tab.headingLabel.setProps({ fontBold: true, fontSize: "xlarge", color: "default", borderWidth: 0 });
    }

    searchDriverSources(driverId: string) {
        this.sourceDriverInfo.search({ id: driverId });
        this.layoutHoursOfServiceTable.mainDataSource.search({ driver_id: driverId });
    }

    sourceFeasibilityAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.setTooltipContent();
            const checklistResults = this.activeRow.get("checklist_results");
            this.setChecklistData(checklistResults);
            const activities = this.activeRow.get("activities");
            this.setListData(activities);
            this.setTimelineData(activities);
            this.layoutFeasibilityMap.mapData = this.activeRow;
            this.tabMap.visible = true;
            this.layoutFeasibilityMap.mapFeasibility.createMapCallback = async () =>  {
                this.layoutFeasibilityMap.createRoute();
            }
            if (MapSettings.get()?.always_show_maps === true || this.layoutFeasibilityMap.mapFeasibility.map instanceof TrimbleMap )
                this.layoutFeasibilityMap.createRoute();
            const driverAssigned: boolean = this.activeRow.get("driver_already_assigned", false);
            const buttonEquipManageAssign = this.tabsetActivities.afterTools.find(button => button.id === "buttonEquipManageAssign");
            buttonEquipManageAssign.enabled = !driverAssigned;
            const buttonSendLoad = this.tabsetActivities.afterTools.find(button => button.id === "buttonSendLoad");
            if (buttonSendLoad) buttonSendLoad.enabled = !!(this.activeRow.get("tractor_id") || this.driverId);
            driverAssigned === true ? buttonEquipManageAssign.disabledTooltip = "Driver is already assigned to this order" : null;
            this.setAppointmentRequired();
            this.addTimezones();
            if (this.activeRow.get("current_movement_status") == "D") {
                this.panelFinalStop.visible = false;
            }
        }
    }

    sourceDriverInfoAfterExecution(event: DataSourceExecutionEvent) {
        const isCanadian = this.sourceDriverInfo.activeRow?.get("country_hos_rules") === "C";
        const numDays = isCanadian ? "30" : "8";
        this.layoutHoursOfServiceTable.isCanadianHOS = isCanadian;
        this.tabHoursOfService.headingLabel.text = `Last ${numDays} Days Hours of Service`;
        this.setLabelVisibility();
    }

    private setTooltipContent() {
        const locationQuickInfo = "lme/dispatch/LocationQuickInfo";
        CommonTooltips.setTooltipContentLayout(this.citystateLastCleared.textCombined, this.activeRow.get("last_cleared_location_id"), locationQuickInfo);
        CommonTooltips.setTooltipContentLayout(this.citystateFinalStop.textCombined, this.activeRow.get("final_stop_location_id"), locationQuickInfo);
        CommonTooltips.setTooltipContentLayout(this.citystatePickup.textCombined, this.activeRow.get("pickup_location_id"), locationQuickInfo);
        CommonTooltips.setTooltipContentLayout(this.citystateDelivery.textCombined, this.activeRow.get("delivery_location_id"), locationQuickInfo);
    }

    setLabelVisibility() {
        const showLabels = this.sourceDriverInfo.summaryData?.getBoolean("show_mc_fields", false);
        this.textboxDutyStatus.visible = this.textboxAvlDriveHours.visible =
        this.textboxAvlOndutyHours.visible = showLabels;
    }

    orderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    orderNumberOnDisplay(event: DataDisplayEvent) {
        if (event.rowData == null)
            return;
        const orderNumberLabel = event.target as Label;
        if (!StringUtil.isEmptyString(orderNumberLabel.text) && orderNumberLabel.text != "--") {
            orderNumberLabel.tooltip = "Click to view order";
            orderNumberLabel.addUnderlineWhenMouseOver();
        } else {
            orderNumberLabel.text = "--";
        }
    }

    imageMoveStatusOnDataDisplay(event: DataDisplayEvent) {
        if (event.rowData == null)
            return;
        const image = event.target as Image;
        let name: ImageName, tooltip: string;
        switch (event.rowData.get("current_movement_status")) {
            case "D":
                name = ImageName.loadDelivery;
                tooltip = "Delivered";
                break;
            case "P":
                name = ImageName.loadInProgress;
                tooltip = "In Progress";
                break;
        }
        image.name = name;
        image.tooltip = tooltip;
    }

    setChecklistData(checklistResults: any[]) {
        this.isPreassignnValid = true;
        const counts = { errors: 0, warnings: 0, valid: 0 };
        const modelRows = checklistResults.map(result => {
            const row = new ModelRow(this.sourceFeasibility.url, false, result);
            const valid = row.getBoolean("valid", false);
            const warn = row.getBoolean("warn", false);
            if (valid) {
                counts.valid++;
            } else if (warn) {
                counts.warnings++;
            } else {
                counts.errors++;
                this.isPreassignnValid = false;
            }
            return row;
        });
        const sorted = this.sortChecklistData(modelRows);
        if (sorted.length > 0) {
            this.tableChecklist.displayData(sorted[0], sorted, 0);
            this.tabChecklist.headingLabel.fillRow = false;
            this.tabChecklist.heading.insert(this.panelChecklistStatus, 1);
            this.panelChecklistStatus.visible = true;
            this.panelChecklistStatus.displayData(new ModelRow(this.sourceFeasibility.url, false, counts), null, 0);
            this.panelError.visible = counts.errors > 0;
            this.panelWarning.visible = counts.warnings > 0;
            this.panelValid.visible = counts.valid > 0;
        }
    }

    sortChecklistData(checklistResults: ModelRow[]) {
        // If both have same validity, sort by warn status (non-warn first)
        // Valid items go to bottom, invalid to top
        return checklistResults.sort((a, b) => {
            const aValid = a.getBoolean("valid", false);
            const bValid = b.getBoolean("valid", false);
            const aWarn = a.getBoolean("warn", false);
            const bWarn = b.getBoolean("warn", false);

            if (aValid === bValid) {
                if (!aValid) { // Both invalid
                    return aWarn === bWarn ? 0 : (aWarn ? 1 : -1);
                }
                return 0;
            }
            return aValid ? 1 : -1;
        });
    }

    tableChecklistOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        this.updateChecklistImage(tableRow);
    }

    updateChecklistImage(tableRow: TableRow) {
        const checklistImage = tableRow.findComponentById("imageStatus") as Image;
        let imageName: ImageName, imageColor: string;
        if (tableRow.data.getBoolean("valid")) {
            imageName = ImageName.circleCheck;
            imageColor = "success";
        } else {
            imageName = ImageName.circleErrorSolid;
            imageColor = tableRow.data.getBoolean("warn", false) ? "caution" : "error";
        }
        checklistImage.name = imageName;
        checklistImage.color = imageColor;
    }

    setListData(activities: any[]) {
        const modelRows = activities.map(activity =>
            new ModelRow(this.sourceFeasibility.url, false, activity)
        );
        if (modelRows.length > 0)
            this.tableList.displayData(modelRows[0], modelRows, 0);
    }

    tableListOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const data = tableRow.data;
        const warningLevel = data.get("warning_level", null);
        if (warningLevel != null) {
            const imageWarningLevel = tableRow.findComponentById("imageWarningLevel") as Image;
            imageWarningLevel.color = warningLevel;
        }
    }
    setTimelineData(activities: any[]) {
        const filteredActivities = this.updateCrossDayActivities(activities);
        const groupedData = this.groupActivites(filteredActivities);
        const timelineData: ModelRow[] = [];
        for (const key in groupedData) {
            timelineData.push(new ModelRow(this.sourceFeasibility.url, false, groupedData[key]));
        }
        this.repeaterTimeline.displayData(timelineData[0], timelineData, 0);
    }

    updateCrossDayActivities(activities: any[]) {
        const filteredActivities = [];
        activities.forEach(activity => {
            if (activity.duration < 0)
                return; // Skip activities that have invalid duration

            const startDate = new Date(activity.start_date);
            const endDate = new Date(activity.end_date);
            const beginningOfStart = DateUtil.setToBeginningOfDay(new Date(startDate));
            const beginningOfEnd = DateUtil.setToBeginningOfDay(new Date(endDate));
            const daysDiff = DateUtil.dateDiff(DatePart.DAY, beginningOfEnd, beginningOfStart);

            if (daysDiff > 0) {
                // Multi-day activity - create segments for each day
                const firstDayActivity = {
                    ...activity,
                    end_date: DateUtil.setToEndOfDay(new Date(startDate))
                };
                filteredActivities.push(firstDayActivity);

                // Account for days between start and end
                for (let i = 1; i < daysDiff; i++) {
                    const currentDay = new Date(beginningOfStart);
                    currentDay.setDate(currentDay.getDate() + i);

                    const middleDayActivity = {
                        ...activity,
                        start_date: DateUtil.setToBeginningOfDay(new Date(currentDay)),
                        end_date: DateUtil.setToEndOfDay(new Date(currentDay))
                    };
                    filteredActivities.push(middleDayActivity);
                }

                if (endDate.getHours() > 0 || endDate.getMinutes() > 1) {
                    const lastDayActivity = {
                        ...activity,
                        start_date: beginningOfEnd,
                        end_date: activity.end_date
                    };
                    filteredActivities.push(lastDayActivity);
                }
            } else {
                filteredActivities.push(activity);
            }
        });
        return filteredActivities;
    }

    groupActivites(activities: any[]): {} {
        const data = {};
        activities.forEach(activity => {
            const dateKey = DateUtil.formatDate(new Date(activity.end_date), "MM-dd-yyyy");
            if (!data[dateKey]) {
                data[dateKey] = {
                    id: dateKey,
                    start_date: new Date(activity.start_date),
                    activities: []
                };
            }
            data[dateKey].activities.push(activity);
        });
        return data;
    }

    repeaterTimelineOnItemDisplay(event: RepeatItemDisplayEvent) {
        const item = event.target as RepeatItem;
        const activities = item.data.get("activities") as any[];
        const panelDates = item.findComponentById("panelDates") as Panel;
        this.setupDates(panelDates);

        activities.forEach((activity, index) => {
            const card = this.createCard(activity);
            if (activity.type == "ON DUTY") {
                const prevActivity = activities[index-1];
                if (prevActivity != null && prevActivity.type == "ON DUTY") {
                    card.borderLeftWidth = 2;
                    card.borderLeftColor = "default.reverse";
                }
            }
            panelDates.add(card);
        });
    }

    setupDates(panelDates: Panel) {
        const userTimeFormat = DateUtil.getUserTimeFormat();
        if (userTimeFormat === "HHmm") {
            for (let i = 0; i < 24; i++) {
                const hourText = panelDates.findComponentById(`labelHour${(i+1)}Text`) as Label;
                hourText.text = DateUtil.formatTime(new Date(new Date().setHours(i, 0)));
                panelDates.findComponentById(`labelHour${(i+1)}TimeUnit`).visible = false;
            }
        }

    }

    createCard(activity) {
        const { description, type, start_date, end_date, warning_level } = activity;
        const dutyType = dutyTypes[type];

        const startDecimal = new Date(start_date).getHours() + (new Date(start_date).getMinutes() / 60);
        const endDecimal = new Date(end_date).getHours() + (new Date(end_date).getMinutes() / 60);
        const leftPercentage = (startDecimal / 24) * 100;
        const widthPercentage = ((endDecimal - startDecimal) / 24) * 100;

        const card = new Panel({
            id: `${type}${start_date}-Card`,
            height: 20,
            paddingTop: 3,
            backgroundColor: warning_level != null ? warning_level : dutyType.color
        });

        card.align = HorizontalAlignment.CENTER;
        card.element.style.position = "absolute";
        card.element.style.top = dutyType.top;
        card.element.style.left = leftPercentage + "%";
        card.element.style.width = widthPercentage + "%";

        const durationString = this.formatDurationFromDecimal(activity.duration);
        if (widthPercentage > 1.25) {
            const activityMessage: Label = this.createLabelForType(type, durationString);
            card.add(activityMessage);
        }
        card.tooltip = widthPercentage < 1.25 ? `${durationString} - ${description}` : description;
        return card;
    }

    createLabelForType(type: string, text: string): Label {
        return new Label({
            text, color: type == "OFF DUTY" ? "default" : "default.reverse",
            fontBold: true, fontSize: "small", padding: 0, wrap: false
        });
    }

    formatDurationFromDecimal(decimalHours: string): string {
        let hours: number;
        let minutes: number;
        if (decimalHours.includes(":")) {
            const [hourStr, minuteStr] = decimalHours.split(':');
            hours = parseInt(hourStr, 10);
            minutes = parseInt(minuteStr, 10);
        } else {
            hours = Math.floor(parseFloat(decimalHours));
            minutes = Math.round((parseFloat(decimalHours) - hours) * 60);
        }

        if (hours === 0) {
            return `${minutes}m`;
        } else if (minutes === 0) {
            return `${hours}h`;
        } else {
            return `${hours}h ${minutes}m`;
        }
    }

    async buttonSendLoadOnClick(event: ClickEvent) {
        try {
            const tractorId = this.activeRow?.get("tractor_id");
            const driverId = this.driverId;
            const response = await SendLoad.validateUnits(tractorId, driverId, null);
            const validation = response?.data?.[0];
            let unitId: string;
            if (validation?.valid_tractor) {
                unitId = tractorId;
            } else if (validation?.valid_driver) {
                unitId = driverId;
            }
            if (!unitId) {
                Snackbar.showWarningSnackbar("No valid mobile comm unit found for this order.");
                return;
            }
            MobileCommSendMessage.showSlideout(null, false, unitId);
        } catch (ex: any) {
            Snackbar.showWarningSnackbar(ex.messages?.[0] ?? ex.message ?? "Failed to validate units.");
        }
    }

    async buttonEquipManageAssignOnClick (event: ClickEvent) {
        const moveId = this.activeRow.get("selected_movement_id");
        const orderId = this.activeRow.get("selected_order_id");
        const tractorId = this.activeRow.get("tractor_id");
        const trailerId = this.activeRow.get("selected_trailer_id");
        const driver1Data = this.activeRow.getFirstLookupModelData("driver_id", null)?.data;
        const driver2Data = this.activeRow.getFirstLookupModelData("driver_two_id", null)?.data;
        const equipGroupId = this.activeRow.get("equipment_group_id");
        const dispatchBy = getDispatchControlValue("dispatch_by");
        const shouldUnassign = dispatchBy == "T"
                    ? !this.activeRow.isNull("tractor_id") && !this.activeRow.isNull("selected_tractor_id")
                     && this.activeRow.get("tractor_id") != this.activeRow.get("selected_tractor_id")
                    : !this.activeRow.isNull("driver_id") && !this.activeRow.isNull("selected_driver_id")
                    && !this.activeRow.get("driver_already_assigned", false);
        const props: PreassignmentSlideoutProps = {
            driver1Data: driver1Data,
            driver2Data: driver2Data,
            tractor: tractorId,
            trailer: trailerId,
            movementId: moveId,
            orderId: orderId,
            preassigned: false,
            equipmentGroupId: equipGroupId,
            loadMatchLock: true,
            unassigning: shouldUnassign,
            sourceEvent: event,
            linkedId: this.linkedId,
            doAfterSlideout: async (name: string, showToast: boolean) => {
                if (showToast) {
                    await this.slideOut();
                    Toast.showToast(Preassignment.getResultsToastPanel(orderId, name));
                }
            }
        };
        const resultRow = !this.isPreassignnValid ? await new PreassignmentDialog("Z").show() : true;

        if (resultRow) {
            if (resultRow instanceof ModelRow) {
                props.feasibilityAssignReason = resultRow;
            }
            Preassignment.showSlideout(props);
        }
    }

    setAppointmentRequired() {
        AppointmentUtil.setAppointmentImage(this.imageFinalApptReq, this.activeRow, "final_stop_appt_required", "final_stop_confirmed");
        AppointmentUtil.setAppointmentImage(this.imagePickupApptReq, this.activeRow, "pickup_appt_required", "pickup_confirmed");
        AppointmentUtil.setAppointmentImage(this.imageDeliveryApptReq, this.activeRow, "delivery_appt_required", "delivery_confirmed");
    }

    imageFinalApptReqOnClick(event: ClickEvent) {
        if (this.activeRow.isNull("current_order_id"))
            return;
        MultiStop.showSlideoutSimple(this.activeRow.get("current_order_id"), true, () => {
            this.searchData();
        });
    }

    imagePickupApptReqOnClick(event: ClickEvent) {
        if (this.activeRow.isNull("selected_order_id"))
            return;
        MultiStop.showSlideoutSimple(this.activeRow.get("selected_order_id"), true, () => {
            this.searchData();
        });
    }

    imageDeliveryApptReqOnClick(event: ClickEvent) {
        if (this.activeRow.isNull("selected_order_id"))
            return;
        MultiStop.showSlideoutSimple(this.activeRow.get("selected_order_id"), true, () => {
            this.searchData();
        });
    }

    addTimezones() {
        this.setTimezoneText(this.textboxFinalStopArriveEarly, "final_stop_timezone_id");
        this.setTimezoneText(this.labelFinalStopArriveLate, "final_stop_timezone_id");
        this.setTimezoneText(this.textboxLastClearedActualArrival, "last_cleared_timezone_id");
        this.setTimezoneText(this.labelLastClearedActualDeparture, "last_cleared_timezone_id");
        this.setTimezoneText(this.labelPickupEarly, "pickup_timezone_id");
        this.setTimezoneText(this.labelPickupLate, "pickup_timezone_id");
        this.setTimezoneText(this.labelDeliveryEarly, "delivery_timezone_id");
        this.setTimezoneText(this.labelDeliveryLate, "delivery_timezone_id");
    }

    setTimezoneText(comp: Label | Textbox, field: string) {
        const timezone = this.activeRow.get(field);
        if (!StringUtil.isEmptyString(comp.text)) {
            comp.text = comp.text + " " + timezone;
        }
    }
}
